import { useEffect, useState } from "react";
import { BellRing, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminAlerts, adminDeleteAlert, apiError } from "@/lib/adminApi";
import { PROVINCE_COLORS } from "@/lib/constants";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATUS_STYLE = { active: "bg-amber-50 text-amber-700", notified: "bg-emerald-50 text-emerald-700", unsubscribed: "bg-slate-100 text-slate-500" };

export function AlertsPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => adminAlerts().then(setData).catch((e) => toast.error(apiError(e, "Could not load alerts")));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this subscriber? They will no longer receive alerts.")) return;
    try { await adminDeleteAlert(id); toast.success("Alert deleted"); load(); } catch (e) { toast.error(apiError(e, "Delete failed")); }
  };

  if (!data) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const rows = filter === "all" ? data.rows : data.rows.filter((r) => r.status === filter);

  return (
    <div className="space-y-4" data-testid="admin-alerts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#C8102E] text-white"><BellRing className="h-5 w-5" /></span>
          <div>
            <p className="font-heading text-base font-semibold text-slate-900">Draw alert subscribers</p>
            <p className="text-xs text-slate-500" data-testid="admin-alerts-total">{data.total} total · {data.counts.active} waiting · {data.counts.notified} notified · {data.counts.unsubscribed} unsubscribed. Emails go out automatically when you save a cutoff or draw round at or below a subscriber's score.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-alerts-filter" className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
            {[["all", "All"], ["active", "Waiting"], ["notified", "Notified"], ["unsubscribed", "Unsubscribed"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={load} data-testid="admin-alerts-refresh" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /> Refresh</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="admin-alerts-empty">No alert subscribers{filter !== "all" ? " with this status" : " yet"}.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>{["Email", "Stream", "Their score", "Cutoff at signup", "Status", "Subscribed", "Notified", "Actions"].map((h) => <th key={h} className="px-3 py-3">{h === "Actions" ? "" : h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={r.alert_id} data-testid={`admin-alert-row-${i}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.email}</td>
                  <td className="px-3 py-2.5"><span className="mr-2 rounded px-1.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[r.province] }}>{r.province}</span>{r.stream_name}</td>
                  <td className="px-3 py-2.5 font-mono-tech font-semibold">{r.score}</td>
                  <td className="px-3 py-2.5 font-mono-tech text-slate-600">{r.cutoff_at_signup ?? "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status]}`}>{r.status === "active" ? "waiting" : r.status}</span>
                    {r.last_error && <span className="ml-2 text-xs text-rose-600" title={r.last_error}>send failed</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{fmt(r.created_at)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{r.notified_at ? `${fmt(r.notified_at)} · cutoff ${r.notified_cutoff}` : "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => remove(r.alert_id)} data-testid={`admin-alert-delete-${i}`} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Delete alert"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
